/**
 * ReviewTenantsModal
 * Centered dialog that shows mapped tenant payloads from suggestions
 * for landlord review/edit before POSTing to /move-in/tenants.
 *
 * Tenant identity (first/last name, email) is copied verbatim from the accepted
 * application, so there is no "confidence" to surface here — the rows are just an
 * editable confirmation of what the applicant submitted.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { createMoveInTenants } from '../../lib/move-in.api';
import type { MoveInSuggestion, TenantPayload } from '../../types/move-in';

// Tenant suggestions come from the applicant.identity contributor with namespaced
// fields like `tenants[0].firstName`. Guarantors (applicant.guarantor → coSigners)
// are NOT tenants and must not appear in this dialog.
const TENANT_FIELD_RE = /^tenants\[(\d+)\]\.(firstName|lastName|email|phone)$/;

interface TenantPayloadRow extends TenantPayload {
  rowKey: string;
  included: boolean;
}

// Group identity suggestions by tenant index to build one payload row per applicant.
function buildTenantPayloadsFromSuggestions(
  suggestions: MoveInSuggestion[]
): TenantPayloadRow[] {
  const byIndex: Record<string, Record<string, string>> = {};

  for (const s of suggestions) {
    if (s.contributor !== 'applicant.identity') continue;
    const match = TENANT_FIELD_RE.exec(s.field);
    if (!match) continue;
    const index = match[1];
    const sub = match[2];
    if (index === undefined || sub === undefined) continue;
    if (!byIndex[index]) byIndex[index] = {};
    byIndex[index][sub] = s.proposedValue != null ? String(s.proposedValue) : '';
  }

  return Object.entries(byIndex)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([index, sub]) => ({
      rowKey: `tenants[${index}]`,
      firstName: sub.firstName ?? '',
      lastName: sub.lastName ?? '',
      email: sub.email ?? '',
      phone: sub.phone ?? '',
      included: true,
    }));
}

interface ReviewTenantsModalProps {
  isOpen: boolean;
  onClose: () => void;
  unitId: string;
  suggestions: MoveInSuggestion[];
  onTenantsAdded: () => void;
}

export function ReviewTenantsModal({
  isOpen,
  onClose,
  unitId,
  suggestions,
  onTenantsAdded,
}: ReviewTenantsModalProps) {
  const [rows, setRows] = useState<TenantPayloadRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset rows when the dialog opens with new suggestions
  useEffect(() => {
    if (isOpen) {
      setRows(buildTenantPayloadsFromSuggestions(suggestions));
      setSubmitError(null);
    }
  }, [isOpen, suggestions]);

  const updateRow = (index: number, patch: Partial<TenantPayloadRow>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const handleConfirm = async () => {
    const included = rows.filter((r) => r.included);
    if (included.length === 0) {
      setSubmitError('Please include at least one tenant.');
      return;
    }

    const tenants: TenantPayload[] = included.map(({ firstName, lastName, email, phone }) => ({
      firstName,
      lastName,
      email,
      ...(phone ? { phone } : {}),
    }));

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await createMoveInTenants(unitId, tenants);
      onTenantsAdded();
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to add tenants');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls =
    'w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-lg max-h-[85vh] overflow-y-auto"
        data-testid="review-tenants-modal"
      >
        <DialogHeader>
          <DialogTitle>Review &amp; Add Tenants</DialogTitle>
          <DialogDescription>
            Review the tenant details from the accepted application, then confirm to add them
            to the unit. They carry over to the lease when you create it.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No tenant data found in the application. You can add tenants manually from the
              Leases tab when you create the lease.
            </p>
          )}

          {rows.map((row, index) => (
            <div
              key={row.rowKey}
              data-testid={`tenant-row-${index}`}
              className={`border rounded-lg p-4 ${
                row.included ? 'border-border' : 'border-border/50 opacity-60'
              }`}
            >
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id={`tenant-include-${index}`}
                  checked={row.included}
                  onChange={(e) => updateRow(index, { included: e.target.checked })}
                  className="h-4 w-4 rounded border-input accent-primary"
                  aria-label={`Include ${row.firstName} ${row.lastName}`}
                />
                <label htmlFor={`tenant-include-${index}`} className="text-sm font-medium text-foreground">
                  Tenant {index + 1}
                </label>
              </div>

              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor={`tenant-first-${index}`}
                      className="block text-xs font-medium text-muted-foreground mb-1"
                    >
                      First name
                    </label>
                    <input
                      id={`tenant-first-${index}`}
                      type="text"
                      value={row.firstName}
                      onChange={(e) => updateRow(index, { firstName: e.target.value })}
                      className={inputCls}
                      disabled={!row.included}
                      aria-label={`First name for tenant ${index + 1}`}
                      data-testid={`tenant-firstName-input-${index}`}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={`tenant-last-${index}`}
                      className="block text-xs font-medium text-muted-foreground mb-1"
                    >
                      Last name
                    </label>
                    <input
                      id={`tenant-last-${index}`}
                      type="text"
                      value={row.lastName}
                      onChange={(e) => updateRow(index, { lastName: e.target.value })}
                      className={inputCls}
                      disabled={!row.included}
                      aria-label={`Last name for tenant ${index + 1}`}
                      data-testid={`tenant-lastName-input-${index}`}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor={`tenant-email-${index}`}
                    className="block text-xs font-medium text-muted-foreground mb-1"
                  >
                    Email
                  </label>
                  <input
                    id={`tenant-email-${index}`}
                    type="email"
                    value={row.email}
                    onChange={(e) => updateRow(index, { email: e.target.value })}
                    className={inputCls}
                    disabled={!row.included}
                    aria-label={`Email for tenant ${index + 1}`}
                    data-testid={`tenant-email-input-${index}`}
                  />
                </div>

                <div>
                  <label
                    htmlFor={`tenant-phone-${index}`}
                    className="block text-xs font-medium text-muted-foreground mb-1"
                  >
                    Phone (optional)
                  </label>
                  <input
                    id={`tenant-phone-${index}`}
                    type="tel"
                    value={row.phone ?? ''}
                    onChange={(e) => updateRow(index, { phone: e.target.value || undefined })}
                    className={inputCls}
                    disabled={!row.included}
                    aria-label={`Phone for tenant ${index + 1}`}
                    data-testid={`tenant-phone-input-${index}`}
                  />
                </div>
              </div>
            </div>
          ))}

          {submitError && (
            <p className="text-sm text-destructive" role="alert">
              {submitError}
            </p>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="default"
            size="sm"
            disabled={isSubmitting || rows.filter((r) => r.included).length === 0}
            onClick={() => void handleConfirm()}
            data-testid="confirm-add-tenants-btn"
            aria-label="Confirm and add tenants"
          >
            {isSubmitting ? 'Adding…' : 'Confirm & Add Tenants'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
